import React, { useState } from 'react';
import CustomModal from '../../util_components/custom_modal';
import CustomSingleSelect from '../../util_components/custom_single_select';
import CustomDatePicker from '../../util_components/custom_date_picker';
import Button from '../../util_components/button';
import { WEEKDAYS, add_leave, api_error_reason, delete_leave, update_rep } from './api';
import type { MemberStatus, TeamLeave, TeamLevel, TeamMember, Weekday } from './api';
import { LEVEL_OPTIONS, STATUS_OPTIONS, validate_rep_edit, weekday_label } from './roster';
import { format_range, ist_today, validate_new_leave, visible_leaves } from './leaves';

export type Notify = (type: 'success' | 'error', message: string) => void;

interface Props {
  member: TeamMember;
  notify: Notify;
  // changed = something was written (profile or leaves) and the roster is stale.
  onClose: (changed: boolean) => void;
}

const INPUT_STYLE: React.CSSProperties = {
  background: 'var(--input-bg)',
  borderColor: 'var(--input-border)',
  color: 'var(--input-text)'
};
const MUTED_STYLE: React.CSSProperties = { color: 'var(--text-muted)' };
const ERROR_STYLE: React.CSSProperties = { color: 'var(--state-error)' };
const SMALL_BTN: React.CSSProperties = { padding: '6px 12px', fontSize: '13px', margin: 0 };

function EditRepModal({ member, notify, onClose }: Props) {
  const [level, set_level] = useState<TeamLevel | ''>(member.level || '');
  const [status, set_status] = useState<MemberStatus>(member.status || 'ACTIVE');
  const [shift_start, set_shift_start] = useState(member.shift_start || '');
  const [shift_end, set_shift_end] = useState(member.shift_end || '');
  const [weekly_off, set_weekly_off] = useState<Weekday[]>(member.weekly_off || []);
  const [leaves, set_leaves] = useState<TeamLeave[]>(member.leaves || []);
  const [leave_from, set_leave_from] = useState('');
  const [leave_to, set_leave_to] = useState('');
  const [leave_note, set_leave_note] = useState('');
  const [error, set_error] = useState<string | null>(null);
  const [leave_error, set_leave_error] = useState<string | null>(null);
  const [busy, set_busy] = useState(false);
  const [changed, set_changed] = useState(false);

  const today = ist_today();
  const shown_leaves = visible_leaves(leaves, today);

  const close = () => onClose(changed);

  const toggle_day = (day: Weekday) =>
    set_weekly_off((prev) =>
      prev.includes(day)
        ? prev.filter((d) => d !== day)
        : WEEKDAYS.filter((d) => d === day || prev.includes(d))
    );

  const save = () => {
    const draft = {
      level: level || null,
      status,
      shift_start: shift_start || null,
      shift_end: shift_end || null,
      weekly_off
    };
    const problem = validate_rep_edit(draft);
    set_error(problem);
    if (problem || !level) return;
    if (
      status === 'LEFT' &&
      member.status !== 'LEFT' &&
      // eslint-disable-next-line no-alert
      !window.confirm(
        `Mark ${member.name} as LEFT? Their claimed tasks are released and they stop getting new ones.`
      )
    ) {
      return;
    }
    set_busy(true);
    update_rep(member.ops_uuid, { ...draft, level })
      .then(({ released_claims }) => {
        const released =
          released_claims > 0
            ? ` · ${released_claims} ${released_claims === 1 ? 'claim' : 'claims'} released`
            : '';
        notify('success', `Saved ${member.name}${released}`);
        onClose(true);
      })
      .catch((e: unknown) => {
        set_error(api_error_reason(e, 'Failed to save'));
        set_busy(false);
      });
  };

  const add = () => {
    const body = { from_date: leave_from, to_date: leave_to, note: leave_note.trim() || undefined };
    const problem = validate_new_leave(body, leaves, today);
    set_leave_error(problem);
    if (problem) return;
    set_busy(true);
    add_leave(member.ops_uuid, body)
      .then((leave) => {
        if (!leave) {
          notify('error', 'Leave response was empty; reloading the team to show what was saved');
          onClose(true);
          return;
        }
        set_leaves((prev) => [...prev, leave]);
        set_leave_from('');
        set_leave_to('');
        set_leave_note('');
        set_changed(true);
        notify('success', 'Leave added');
      })
      .catch((e: unknown) => set_leave_error(api_error_reason(e, 'Failed to add leave')))
      .finally(() => set_busy(false));
  };

  const remove = (leave: TeamLeave) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove leave ${format_range(leave.from_date, leave.to_date)}?`)) return;
    set_busy(true);
    delete_leave(member.ops_uuid, leave.id)
      .then(() => {
        set_leaves((prev) => prev.filter((l) => l.id !== leave.id));
        set_changed(true);
        notify('success', 'Leave removed');
      })
      .catch((e: unknown) => set_leave_error(api_error_reason(e, 'Failed to remove leave')))
      .finally(() => set_busy(false));
  };

  return (
    <CustomModal show close={close} title={`Edit ${member.name}`}>
      <div className="flex flex-col gap-4" style={{ color: 'var(--text-primary)' }}>
        <div className="flex flex-wrap gap-4">
          <CustomSingleSelect
            id="sales-rep-level"
            label="Level"
            name="level"
            value={level}
            defaultOption="Select level"
            options={LEVEL_OPTIONS}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              set_level(e.target.value as TeamLevel | '')
            }
          />
          <CustomSingleSelect
            id="sales-rep-status"
            label="Status"
            name="status"
            value={status}
            defaultOption="Select status"
            options={STATUS_OPTIONS}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              set_status(e.target.value as MemberStatus)
            }
          />
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <label
            htmlFor="sales-rep-shift-start"
            className="flex flex-col text-xs"
            style={MUTED_STYLE}
          >
            Shift start (IST)
            <input
              id="sales-rep-shift-start"
              type="time"
              aria-label="Shift start"
              value={shift_start}
              onChange={(e) => set_shift_start(e.target.value)}
              className="mt-1 h-[34px] w-[130px] rounded border px-2 text-sm"
              style={INPUT_STYLE}
            />
          </label>
          <label
            htmlFor="sales-rep-shift-end"
            className="flex flex-col text-xs"
            style={MUTED_STYLE}
          >
            Shift end (IST)
            <input
              id="sales-rep-shift-end"
              type="time"
              aria-label="Shift end"
              value={shift_end}
              onChange={(e) => set_shift_end(e.target.value)}
              className="mt-1 h-[34px] w-[130px] rounded border px-2 text-sm"
              style={INPUT_STYLE}
            />
          </label>
          <span className="pb-2 text-xs" style={MUTED_STYLE}>
            An end earlier than the start crosses midnight.
          </span>
        </div>

        <fieldset>
          <legend className="mb-1 text-xs" style={MUTED_STYLE}>
            Weekly off
          </legend>
          <div className="flex flex-wrap gap-3">
            {WEEKDAYS.map((d) => (
              <label
                key={d}
                htmlFor={`sales-rep-off-${d}`}
                className="inline-flex items-center gap-1 text-sm"
              >
                <input
                  id={`sales-rep-off-${d}`}
                  type="checkbox"
                  checked={weekly_off.includes(d)}
                  onChange={() => toggle_day(d)}
                />
                {weekday_label(d)}
              </label>
            ))}
          </div>
        </fieldset>

        {error ? (
          <div className="text-sm" style={ERROR_STYLE} role="alert">
            {error}
          </div>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button type="secondary" onClick={close} custom_style={SMALL_BTN}>
            Cancel
          </Button>
          <Button onClick={save} disabled={busy} custom_style={SMALL_BTN}>
            Save
          </Button>
        </div>

        <section className="border-t pt-3" style={{ borderColor: 'var(--border-default)' }}>
          <h4 className="mb-2 font-semibold">Leaves</h4>
          <p className="mb-2 text-xs" style={MUTED_STYLE}>
            Leaves are saved immediately.
          </p>
          {shown_leaves.length === 0 ? (
            <div className="mb-2 text-xs" style={MUTED_STYLE}>
              No upcoming leave.
            </div>
          ) : (
            <ul className="mb-3 flex flex-col gap-1">
              {shown_leaves.map((l) => (
                <li key={l.id} className="flex items-center justify-between gap-2 text-sm">
                  <span>
                    {format_range(l.from_date, l.to_date)}
                    {l.note ? <span style={MUTED_STYLE}> · {l.note}</span> : null}
                  </span>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => remove(l)}
                    className="text-xs underline"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--state-error)'
                    }}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex flex-wrap items-end gap-3">
            <CustomDatePicker
              id="sales-rep-leave-from"
              label="From"
              value={leave_from}
              minDate={today}
              handleChange={(d: string) => set_leave_from(d)}
            />
            <CustomDatePicker
              id="sales-rep-leave-to"
              label="To"
              value={leave_to}
              minDate={leave_from || today}
              handleChange={(d: string) => set_leave_to(d)}
            />
            <label
              htmlFor="sales-rep-leave-note"
              className="flex flex-col text-xs"
              style={MUTED_STYLE}
            >
              Note
              <input
                id="sales-rep-leave-note"
                type="text"
                aria-label="Leave note"
                value={leave_note}
                maxLength={255}
                onChange={(e) => set_leave_note(e.target.value)}
                className="mt-1 h-[34px] w-[180px] rounded border px-2 text-sm"
                style={INPUT_STYLE}
              />
            </label>
            <Button onClick={add} disabled={busy} custom_style={SMALL_BTN}>
              Add leave
            </Button>
          </div>
          {leave_error ? (
            <div className="mt-2 text-sm" style={ERROR_STYLE} role="alert">
              {leave_error}
            </div>
          ) : null}
        </section>
      </div>
    </CustomModal>
  );
}

export default EditRepModal;
